/* eslint-disable */
import { extractNumeric } from "@/Pages/AIFMSUMMARY/helper";
import { useCallback, useEffect, useMemo, useState, memo } from "react";
import issued from '@/Images/issued.svg';
import produced from '@/Images/produced.svg';
import primaryPackaged from '@/Images/primaryPack.svg';
import secondaryPackaged from '@/Images/secondaryPack.svg';
import dispatched from '@/Images/externalDispatch.svg';
import wastage from '@/Images/wastage.svg';
import noMachineIcon from '@/Images/noweigh.svg';
import { Tooltip } from "@mui/material";

const imagesMap = {
    'issued': issued,
    'produced': produced,
    'primaryPackaged': primaryPackaged,
    'secondaryPackaged': secondaryPackaged,
    'dispatch': dispatched,
    'wastage': wastage
}

const formatWeight = (valStr) => {
    let cleanValStr = String(valStr).replace(/₹/g, '').trim();
    if (!cleanValStr || cleanValStr === '-') return { value: cleanValStr, unit: '' };

    const measurementPattern = /^[\d,]*\.?\d+(\s+[a-zA-Z/%]+)?$/;
    if (!measurementPattern.test(cleanValStr)) {
        return { value: valStr, unit: '' };
    }

    const num = extractNumeric(cleanValStr);
    if (isNaN(num)) return { value: cleanValStr, unit: '' };

    const lastSpaceIndex = cleanValStr.lastIndexOf(' ');
    let unit = '';
    if (lastSpaceIndex !== -1) {
        unit = cleanValStr.substring(lastSpaceIndex + 1);
    }

    let finalValue = num;
    let finalUnit = unit;

    if (unit?.toUpperCase() === 'KG' && num >= 1000) {
        finalValue = num / 1000;
        finalUnit = 'TON';
    }

    const hasRupee = String(valStr).includes('₹');

    return {
        value: (hasRupee ? '₹' : '') + finalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit: finalUnit
    };
};

const formatDualValue = (value, hideUnit = false) => {
    if (typeof value !== 'string') return value;

    const renderFraction = (val) => {
        if (!val.includes('/')) {
            const { value: v, unit: u } = formatWeight(val);
            return u ? `${v} ${u}` : v;
        }

        const parts = val.split('/');
        if (parts.length !== 2) return val;

        let numerator = parts[0].trim();
        const hasRupeeNum = numerator.includes('₹');
        const denominatorPart = parts[1].trim();
        const hasRupeeDen = denominatorPart.includes('₹');

        const lastSpaceIndex = denominatorPart.lastIndexOf(' ');
        let denominator = denominatorPart;
        let unit = '';

        if (lastSpaceIndex !== -1) {
            denominator = denominatorPart.substring(0, lastSpaceIndex);
            unit = denominatorPart.substring(lastSpaceIndex + 1);
        }

        const numLastSpace = numerator.lastIndexOf(' ');
        if (numLastSpace !== -1) {
            const numTail = numerator.substring(numLastSpace + 1);
            if (numTail && /^[a-zA-Z%]+$/.test(numTail)) {
                numerator = numerator.substring(0, numLastSpace).trim();
            }
        }

        const formattedNum = formatWeight(`${numerator} ${unit}`);
        const formattedDen = formatWeight(`${denominator} ${unit}`);

        return (
            <span className='inline-flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-gray-50/50 transition-colors align-middle'>
                <span className='flex flex-col items-start justify-center min-w-[20px]'>
                    <span className='border-b border-current text-[14px] font-semibold leading-none pb-[1px] w-full text-left tracking-tight whitespace-nowrap'>
                        {formattedNum.value} <span className="text-[10px] uppercase">{formattedNum.unit}</span>
                    </span>
                    <span className='text-[14px] text-gray-600 font-medium leading-none pt-[1px] w-full text-left tracking-tight whitespace-nowrap'>
                        {formattedDen.value} <span className="text-[10px] uppercase">{formattedDen.unit}</span>
                    </span>
                </span>
            </span>
        );
    };

    if (value.includes('→')) {
        return (
            <span className="inline-flex items-center gap-1">
                {value.split('→').map((part, index, array) => (
                    <span key={index} className="inline-flex items-center gap-1">
                        {renderFraction(part.trim())}
                        {index < array.length - 1 && <span className="text-gray-400 font-bold select-none text-[12px] leading-none mb-0.5">→</span>}
                    </span>
                ))}
            </span>
        );
    }

    return renderFraction(value);
};

const CellChips = memo(({ value }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (typeof value !== 'string' || !value.includes(',')) return value;

    const splitValue = value.split(',');
    if (splitValue.length === 0) return value;

    const visibleChips = isExpanded ? splitValue : splitValue.slice(0, 2);
    const hasMore = splitValue.length > 2;

    return (
        <div className='flex flex-wrap gap-1'>
            {visibleChips.map((val, idx) => (
                <span key={idx} className='inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FDE6AD] text-gray-800 border border-[#00000033] shadow-sm'>
                    {val.trim()}
                </span>
            ))}
            {hasMore && (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className='inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 border border-blue-300 shadow-sm cursor-pointer hover:bg-blue-200 transition-colors'
                >
                    {isExpanded ? 'Show Less' : `+${splitValue.length - 2} more`}
                </span>
            )}
        </div>
    );
});

const COLOR_MAP = {
    green: '#16a34a',
    orange: '#f97316',
    red: '#dc2626',
    black: '#111827',
    grey: '#6b7280',
    gray: '#6b7280',
    blue: '#2563eb',
    purple: '#9333ea',
};
const resolveColor = (color, fallback) => COLOR_MAP[color] || color || fallback;

// `format` decides how `value` is printed. Without it the old formatting is used and `unit`
// is ignored — cost cells already send `unit: "kg"` inside a value that has it.
const formatCellValue = (value, format, unit) => {
    if (value === null || value === undefined) return '-';
    if (!format) return formatDualValue(value);
    const num = typeof value === 'number' ? value : extractNumeric(String(value));
    const withUnit = (v) => (unit ? <>{v} <span className="text-[10px] uppercase">{unit}</span></> : v);
    switch (format) {
        case 'text':
            return withUnit(value);
        case 'number':
            return isNaN(num) ? value : withUnit(num.toLocaleString(undefined, { maximumFractionDigits: 2 }));
        case 'percent':
            return isNaN(num) ? value : `${num.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
        case 'currency':
            return isNaN(num) ? value : withUnit('₹' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        default:
            return formatDualValue(value);
    }
};

const RICH_TIME_RE = /(\d{1,2} [A-Z][a-z]{2} \d{1,2}:\d{2} [AP]M|\d{1,2}:\d{2} [AP]M|\d+h \d{1,2}m|\d+m \d{1,2}s|\d+(?:\.\d+)? s\/QR|\b\d+s\b)/g;
const RICH_TIME_COLOR = '#2f5d8a';
const renderRich = (text) => String(text ?? '').split(RICH_TIME_RE).map((part, i) => (
    i % 2 === 1
        ? <span key={i} className='whitespace-nowrap font-semibold' style={{ color: RICH_TIME_COLOR }}>{part}</span>
        : <span key={i}>{part}</span>
));

// Single renderer for every object cell — each part renders only when its key is present.
const FlexCell = ({ cellData, isNameColumn, chips, showDummyTag }) => {
    const isLink = Boolean(cellData?.link);
    const color = isLink ? undefined : (resolveColor(cellData?.color) || cellData?.extra?.color || '#374151');
    const tooltip = Array.isArray(cellData?.tooltip) ? cellData.tooltip.filter(Boolean).join(' · ') : (cellData?.tooltip || '');
    const progress = cellData?.progress;
    const flags = Array.isArray(cellData?.flags) ? cellData.flags.filter((f) => f?.label) : [];
    const rich = Boolean(cellData?.richText);

    const content = (
            <div
                className={`flex flex-col items-start justify-center text-left text-[14px] ${isLink ? 'cursor-pointer hover:underline text-blue-700' : ''}`}
                style={color ? { color } : {}}
                {...(isLink ? { onClick: () => window.open(cellData.link, '_blank') } : {})}
            >
                {chips || (
                    <div className='flex items-center justify-start gap-2'>
                        <span className={cellData?.bold ? 'font-semibold' : ''}>
                            {isNameColumn ? (cellData?.value ?? '-') : formatCellValue(cellData?.value, cellData?.format, cellData?.unit)}
                        </span>
                        {cellData?.isAssumption && (
                            <Tooltip title="Assumed value (Skipped by operator)">
                                <div className='flex bg-white rounded-md overflow-hidden gap-1 relative items-center w-5 h-5'>
                                    <div className='z-10 absolute flex justify-center w-full h-full'>
                                        <div className='h-full w-[1.5px] bg-red-700 -rotate-45' />
                                    </div>
                                    <img src={noMachineIcon} alt='noMachineIcon' className='inline w-full h-full' />
                                </div>
                            </Tooltip>
                        )}
                        {showDummyTag && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#0F766E] text-white rounded border border-gray-300">
                                Dummy Data
                            </span>
                        )}
                    </div>
                )}
                {cellData?.subText && (
                    <div
                        // Wraps instead of stretching the column ("Complete pressed 51h 24m after last crate"),
                        // unless the backend sends its own classes (subTextClass, e.g. 'whitespace-nowrap'
                        // so a time range "05 Oct 05:07 PM → 07 Oct 08:32 PM" stays on one line).
                        className={`text-[10.5px] font-medium mt-0.5 leading-snug ${cellData?.subTextClass || 'whitespace-normal break-words max-w-[170px]'}`}
                        style={{ color: resolveColor(cellData?.subTextColor, '#6b7280') }}
                    >
                        {rich ? renderRich(cellData.subText) : cellData.subText}
                    </div>
                )}
                {Array.isArray(cellData?.data) && cellData.data.map((item, idx) => (
                    <div
                        key={idx}
                        // rich lines are inline text so the message wraps naturally around unbroken times
                        // item.className (sent by the backend) replaces the default wrapping of a rich line.
                        className={rich && !item.link
                            ? `leading-snug mt-0.5 ${item.className || 'whitespace-normal break-words max-w-[190px]'}`
                            : `flex gap-1 items-center ${item.link ? 'cursor-pointer hover:underline font-semibold' : ''} ${item.className || ''}`}
                        style={{ color: item.color || '#6b7280', fontSize: ((item.size || 14) + 'px') }}
                        // A line can carry its own link (e.g. the PO number under the store name).
                        {...(item.link ? { onClick: (e) => { e.stopPropagation(); window.open(item.link, '_blank'); } } : {})}
                    >
                        {item.label && <span className="font-semibold">{item.label}:</span>}
                        {/* raw: print as sent (PO numbers, counts) - formatDualValue would print "118" as "118.00". */}
                        <span>{rich && item.raw && !item.link ? renderRich(item.value) : (item.raw ? item.value : formatDualValue(item.value))}</span>
                    </div>
                ))}
                {progress && (
                    // No hover on the bar: the % is printed beside it (progress.text) where wanted.
                    <div className='flex items-center gap-1.5 mt-1'>
                        <div className='w-[86px] h-[5px] rounded-full bg-gray-200 overflow-hidden'>
                            <div
                                className='h-full rounded-full'
                                style={{
                                    width: `${Math.min(Math.max(Number(progress.percent) || 0, 0), 100)}%`,
                                    backgroundColor: resolveColor(progress.color, '#16a34a'),
                                }}
                            />
                        </div>
                        {progress.text && (
                            <span className='text-[10.5px] font-semibold' style={{ color: resolveColor(progress.color, '#16a34a') }}>
                                {progress.text}
                            </span>
                        )}
                    </div>
                )}
                {flags.length > 0 && (
                    <div className='flex flex-wrap items-center justify-start gap-1 mt-1 max-w-[150px]'>
                        {flags.map((flag) => {
                            const flagColor = resolveColor(flag.color, '#6b7280');
                            return (
                                <span
                                    key={flag.code || flag.label}
                                    title={flag.tip || ''}
                                    className='px-1.5 py-[1px] rounded text-[9.5px] font-semibold border whitespace-nowrap'
                                    style={{ color: flagColor, borderColor: flagColor, backgroundColor: `${flagColor}14` }}
                                >
                                    {flag.label}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
    );

    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

export const useTableData = (rawData, parentTab, search, isLoading, selectedTab) => {
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);

    const getChips = useCallback((value) => {
        return <CellChips value={value} />;
    }, []);

    useEffect(() => {
        if (isLoading) {
            setColumns([]);
            setRows([]);
        }
    }, [isLoading]);

    useEffect(() => {
        if (rawData?.columns?.length > 0 || rawData?.data?.length > 0) {
            setColumns(rawData?.columns);
            setRows(rawData?.data);
        }
    }, [rawData]);

    const getColumns = useMemo(() => {
        if (!columns?.length) return [];
        return columns.map((column, index) => {
            const isNameColumn = index === 0 || [
                'productName', 'itemName', 'ingredientName', 'intermediateName',
                'Product Name', 'Item Name', 'Ingredient Name', 'Intermediate Name', 'Ingredient Issued'
            ].includes(column?.key);
            return {
                ...column,
                isNameColumn,
                title: column?.label,
                searchable: isNameColumn ? true : column?.searchable,
                customCol: true,
                sortable: true,
                width: isNameColumn ? 220 : (columns?.length > 8 ? 110 : 130),
            };
        });
    }, [columns]);

    const filterData = useMemo(() => {
        if (!rows?.length) return [];
        if (!search?.searchValue) return rows;

        const searchLower = String(search?.searchValue).toLowerCase().trim();

        return rows.filter((item) => {
            const searchableText = Object.values(item)
                .map((value) => {
                    if (value === null || value === undefined) return '';

                    if (typeof value === 'object' && value !== null) {
                        if ('value' in value) {
                            return String(value.value || '');
                        }
                        return JSON.stringify(value);
                    }

                    return String(value);
                })
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return searchableText.includes(searchLower);
        });

        // if (!search?.searchValue || !search?.value) return rows;
        // const searchLower = String(search?.searchValue).toLowerCase();
        // return rows.filter((item) => {
        //     const itemValue = String(item?.[search?.value] ?? '').toLowerCase();
        //     return itemValue.includes(searchLower);
        // });
    }, [rows, search?.searchValue, search?.value]);


    const customRowData = useMemo(() => {
        const base = {};
        getColumns.forEach((column, index) => {
            base[column.key] = (product) => {
                const cellData = product?.[column.key];
                const isDummy = product?.isDummy;
                const showDummyTag = isDummy && index === 0;

                // Plain string / number cells are wrapped so every cell goes through FlexCell.
                const cell = (cellData && typeof cellData === 'object' && cellData.value !== undefined)
                    ? cellData
                    : { value: cellData };

                return (
                    <FlexCell
                        cellData={cell}
                        isNameColumn={column.isNameColumn}
                        chips={column?.chips ? getChips(cell.value ?? '-') : null}
                        showDummyTag={showDummyTag}
                    />
                );
            };
        });
        return base;
    }, [getColumns, search, getChips]);

    const sortInfo = useMemo(() => {
        const info = {};
        getColumns.forEach((column) => {
            const isNameColumnForSort = column.isNameColumn || ['unit'].includes(column.key);
            const detectedType = column?.dataType || (isNameColumnForSort ? 'string' : 'number');
            const cellSortsAsNumber = () => !isNameColumnForSort && filterData.some((row) => row?.[column.key]?.sortValue !== undefined);

            info[column.key] = {
                type: cellSortsAsNumber(column) ? 'number' : detectedType,
                customValue: true,
                value: (obj) => {
                    const cellData = obj?.[column.key];
                    // Values that aren't plain numbers ("1h 02m", "Not scanned") sort on the
                    // number the backend sends in sortValue; missing sorts lowest.
                    if (!column.isNameColumn && cellData && typeof cellData === 'object' && 'sortValue' in cellData) {
                        return cellData.sortValue ?? -1;
                    }
                    let rawValue = (cellData && typeof cellData === 'object') ? cellData.value : cellData;

                    if (rawValue === undefined || rawValue === null || rawValue === '') {
                        return detectedType === 'number' ? 0 : '';
                    }

                    if (typeof rawValue === 'string') {
                        if (rawValue.includes('/') || detectedType === 'number') {
                            const firstPart = rawValue.split('/')[0];
                            const extractedNum = extractNumeric(firstPart);
                            if (!isNaN(extractedNum)) return extractedNum;
                        }
                    } else if (typeof rawValue === 'number') {
                        return rawValue;
                    }

                    return rawValue ?? '';
                },
            };
        });
        return info;
    }, [getColumns, filterData]);


    const customCol = useCallback((column) => {
        return (
            <div
                className="flex flex-col gap-1"
            >
                <div className="flex items-center gap-2">
                    {column?.image && (
                        <img src={imagesMap[column?.image]} alt={column?.image} className="w-4 h-4" />
                    )}
                    <p className={`text-left w-full font-bold text-[11px] leading-tight break-words ${column?.link ? 'cursor-pointer hover:underline text-blue-700' : 'text-gray-700'}`}
                        {...(column?.link ? { onClick: () => window.open(column?.link, '_blank') } : {})}>{column?.label ?? ''}</p>
                </div>
                {Array.isArray(column?.data) && column?.data?.length > 0 && column?.data?.map((item, idx) => (
                    <div key={idx} className="flex flex-col">
                        <p className={`flex gap-1 justify-start m-0 text-left items-center ${item?.link ? 'cursor-pointer hover:underline text-blue-700' : 'text-gray-700'}`}
                            {...(item?.link ? { onClick: () => window.open(item?.link, '_blank') } : {})}
                            style={{ color: item.color || '#6b7280', fontSize: ((item.size || 10) + 'px') }}
                        >

                            {/* <span className="text-[11px]">{item?.label}</span> : <span className="text-[11px]">{item?.value}</span> */}
                            {item.label && <span className="font-semibold">{item.label}:</span>}
                            {/* item.unit is printed beside the value ("142 kg/hr") — kept separate because
                                formatDualValue draws any "/" inside a value as a fraction. */}
                            <span className="font-bold text-gray-900" style={item.raw && item.color ? { color: item.color } : {}}>
                                {item.raw ? item.value : formatDualValue(item.value)}
                            </span>
                            {item.unit && <span>{item.unit}</span>}
                        </p>
                    </div>
                ))}
            </div>
        );
    }, []);

    const getTableInfo = useCallback(() => {
        return {
            data: filterData,
            tableRows: customRowData,
            tableColumns: getColumns,
            sortInfo,
            customCol,
        };
    }, [rows, getColumns, customRowData, sortInfo, search, customCol, filterData]);

    const dropDownItems = useMemo(() => {
        if (!getColumns.length) return [];
        return getColumns
            .filter((column) => column?.searchable)
            .map((column) => ({
                label: column?.label,
                value: column?.key,
                placeholder: `Search by ${column?.label}`,
            }));
    }, [getColumns]);

    return {
        getTableInfo,
        dropDownItems
    };
}